import * as React from 'react';

import { cva, VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const Table = React.forwardRef<HTMLTableElement, React.HTMLAttributes<HTMLTableElement>>(
  ({ className, ...props }, ref) => (
    <div className="relative w-full overflow-auto">
      <table ref={ref} className={cn('w-full caption-bottom text-sm', className)} {...props} />
    </div>
  )
);
Table.displayName = 'Table';

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead ref={ref} className={cn('[&_tr]:border-b', className)} {...props} />
));
TableHeader.displayName = 'TableHeader';

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn(
      '[&_tr:last-child]:border-0 [&_tr:has(+tr[data-sentinel]:last-child)]:border-b-0',
      className
    )}
    {...props}
  />
));
TableBody.displayName = 'TableBody';

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn('border-t bg-muted/50 font-medium [&>tr]:last:border-b-0', className)}
    {...props}
  />
));
TableFooter.displayName = 'TableFooter';

const TableRow = React.forwardRef<HTMLTableRowElement, React.HTMLAttributes<HTMLTableRowElement>>(
  ({ className, ...props }, ref) => (
    <tr
      ref={ref}
      className={cn(
        'border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted',
        className
      )}
      {...props}
    />
  )
);
TableRow.displayName = 'TableRow';

const tableHeadVariants = cva(
  'h-12 overflow-hidden py-3 pl-2 pr-2 text-left align-middle font-medium text-muted-foreground first:pl-4 last:pr-4 [&:has([role=checkbox])]:p-0',
  {
    variants: {
      variant: {
        default: 'bg-muted',
        generateInvoices: 'border border-border bg-accent px-4',
        // conflictResolution: Identical to default but exists as a named variant
        // for semantic clarity and grouping logic in data-table.tsx
        conflictResolution: 'bg-muted',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement> & VariantProps<typeof tableHeadVariants>
>(({ className, variant, ...props }, ref) => (
  <th ref={ref} className={cn(tableHeadVariants({ variant }), className)} {...props} />
));
TableHead.displayName = 'TableHead';

const tableCellVariants = cva(
  'overflow-hidden py-4 pl-2 pr-2 align-middle transition-colors first:pl-4 last:pr-4 has-[[role=checkbox]]:p-0',
  {
    variants: {
      variant: {
        default: 'bg-background group-hover:bg-surface',
        generateInvoices: `
        border border-border bg-background px-4 py-3.5
        has-[[role=checkbox]]:bg-accent has-[[role=checkbox]]:px-4
        has-[[type=button]]:py-0 has-[[type=number]]:py-0
      `,
        // conflictResolution: Identical to default but exists as a named variant
        // for semantic clarity and grouping logic in data-table.tsx
        conflictResolution: 'bg-background group-hover:bg-surface',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement> & VariantProps<typeof tableCellVariants>
>(({ className, variant, ...props }, ref) => (
  <td ref={ref} className={cn(tableCellVariants({ variant }), className)} {...props} />
));
TableCell.displayName = 'TableCell';

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption ref={ref} className={cn('mt-4 text-sm text-muted-foreground', className)} {...props} />
));
TableCaption.displayName = 'TableCaption';

export { Table, TableBody, TableCaption, TableCell, TableFooter, TableHead, TableHeader, TableRow };
